/**
 * TNK-130 phase F — drafts management. Three-tab view:
 *
 *   Active     — parsing / enriching / ready (default).
 *   Committed  — drafts that promoted into inventory; shows committedAt.
 *   All        — everything including cancelled.
 *
 * Per-row duplicate flag fires when another draft in the current view
 * shares the same sourceFilename + sourceFormat + totalRows tuple —
 * the practical "I re-uploaded the same CSV" signal. Active drafts in
 * the dupe group get a yellow DUPLICATE pill so the operator can
 * delete or resume just one of them.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  confirmImportDraft,
  deleteImportDraft,
  listImportDrafts,
  type ApiImportDraft,
} from "@/features/importsApi";

type Tab = "active" | "committed" | "all";

const ACTIVE_STATUSES = new Set(["parsing", "enriching", "ready"]);

interface SearchParams {
  tab?: Tab;
}

export const Route = createFileRoute("/tools/imports/")({
  component: ImportsIndex,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const t = search.tab;
    return {
      tab: t === "committed" || t === "all" ? t : "active",
    };
  },
});

function ImportsIndex() {
  const search = useSearch({ from: "/tools/imports/" }) as SearchParams;
  const tab: Tab = search.tab ?? "active";
  const [drafts, setDrafts] = useState<ApiImportDraft[] | null>(null);
  const [error, setError] = useState<string>("");
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  // TNK-133 polish — inline confirm chip on the row instead of the
  // native `window.confirm` system dialog. Set to the draft id while
  // its row is in confirm mode; cleared on cancel or after delete.
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Always pull the wider set so the per-tab counts on the tab strip
  // populate immediately (without it, the Committed badge stays at 0
  // until the operator clicks the tab — confusing). Cheap; drafts
  // are bounded by operator activity, not catalog size.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await listImportDrafts({ includeAll: true });
        if (!cancelled) setDrafts(list);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? "Load failed.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    if (!drafts) return null;
    if (tab === "active")
      return drafts.filter((d) => ACTIVE_STATUSES.has(d.status));
    if (tab === "committed")
      return drafts.filter((d) => d.status === "committed");
    return drafts;
  }, [drafts, tab]);

  // Duplicate signal: same filename + format + totalRows in the visible
  // set. Pasted-CSV drafts (no filename) never count as duplicates
  // because the name fallback would group every paste together.
  const duplicateIds = useMemo(() => {
    const out = new Set<string>();
    if (!visible) return out;
    const buckets = new Map<string, string[]>();
    for (const d of visible) {
      if (!d.sourceFilename) continue;
      const key = `${d.sourceFilename}|${d.sourceFormat}|${d.totalRows}`;
      const arr = buckets.get(key) ?? [];
      arr.push(d.id);
      buckets.set(key, arr);
    }
    for (const ids of buckets.values()) {
      if (ids.length > 1) for (const id of ids) out.add(id);
    }
    return out;
  }, [visible]);

  const counts = useMemo(() => {
    if (!drafts) return { active: 0, committed: 0, all: 0 };
    return {
      active: drafts.filter((d) => ACTIVE_STATUSES.has(d.status)).length,
      committed: drafts.filter((d) => d.status === "committed").length,
      all: drafts.length,
    };
  }, [drafts]);

  // Quick accept: one-click confirm from the list for drafts that are
  // ready with zero failed rows. Commits with no bin and no lot (the
  // server treats null as unassigned). Operators who want a bin / lot
  // assignment still go through the wizard's Resume → confirm flow.
  const [quickAccepting, setQuickAccepting] = useState<Set<string>>(new Set());
  const handleQuickAccept = async (id: string, label: string) => {
    if (quickAccepting.has(id)) return;
    if (
      !window.confirm(
        `Quick-accept "${label}" with no bin and no lot? Cards land in Unlisted status — you can assign later.`,
      )
    )
      return;
    setQuickAccepting((s) => new Set(s).add(id));
    setError("");
    try {
      await confirmImportDraft(id, { binId: null, lotId: null });
      // Refresh the list — the draft flips to `committed` and would no
      // longer match the Active tab if that's where the operator is.
      const next = await listImportDrafts({ includeAll: true });
      setDrafts(next);
    } catch (err) {
      setError((err as Error).message ?? "Quick-accept failed.");
    } finally {
      setQuickAccepting((s) => {
        const out = new Set(s);
        out.delete(id);
        return out;
      });
    }
  };

  // TNK-133 polish — confirm flow moved to an inline chip on the row
  // (see `deleteConfirmId`). This helper assumes the operator already
  // pressed "Yes, delete" on that chip; it just runs the delete.
  // `label` is retained for the error-message context only.
  const handleDelete = async (id: string, _label: string) => {
    if (deleting.has(id)) return;
    setDeleting((s) => new Set(s).add(id));
    setDeleteConfirmId(null);
    setError("");
    try {
      await deleteImportDraft(id);
      setDrafts((prev) => prev?.filter((d) => d.id !== id) ?? prev);
    } catch (err) {
      setError((err as Error).message ?? "Delete failed.");
    } finally {
      setDeleting((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    // Section shell + full-width body — same pattern as /tools/storage-bins,
    // /tools/supplies, etc. No inner column wrapper; back-button + header
    // align flush left with the rest of the operator tools area.
    <section className="flex h-full flex-col space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <BackButton fallback="/tools" />
          <h1 className="text-2xl font-bold text-foreground">Card imports</h1>
          <p className="text-sm text-muted-foreground">
            In-progress + committed imports. Resume an active draft to finish
            reviewing it, or check Committed for what's already landed in
            inventory.
          </p>
        </div>
        <Link to="/tools/imports/new" className="shrink-0">
          <PrimaryButton
            onClick={() => undefined}
            label="New import"
            leadingIcon={<Plus size={14} />}
            className="whitespace-nowrap"
          />
        </Link>
      </div>

      {/* Tab strip — only Active needs the smaller request, the others
          pull the wider list and filter client-side so flipping between
          tabs is fast. */}
      <div className="flex gap-1 border-b border-border">
        <TabButton tab="active" current={tab} count={counts.active}>
          Active
        </TabButton>
        <TabButton tab="committed" current={tab} count={counts.committed}>
          Committed
        </TabButton>
        <TabButton tab="all" current={tab} count={counts.all}>
          All
        </TabButton>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {visible === null && !error && (
        <div className="py-12 text-center text-muted-foreground">
          <Loader2 size={20} className="mx-auto animate-spin" />
          <p className="mt-3 text-sm">Loading drafts…</p>
        </div>
      )}

      {visible && visible.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-12 text-center">
          <FileText size={28} className="mx-auto text-muted-foreground/60" />
          <p className="mt-3 text-sm font-medium text-foreground">
            {tab === "active"
              ? "No imports in flight."
              : tab === "committed"
                ? "No committed imports yet."
                : "No imports."}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {tab === "committed"
              ? "Drafts you've confirmed will appear here with the date they landed."
              : "Start one and your CSV stays staged here until you confirm."}
          </p>
          {tab !== "committed" && (
            <Link
              to="/tools/imports/new"
              className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90"
            >
              Start an import
            </Link>
          )}
        </div>
      )}

      {visible && visible.length > 0 && (
        <ul className="flex flex-col gap-2">
          {visible.map((d) => {
            const label = d.sourceFilename ?? "Pasted CSV";
            const isDeleting = deleting.has(d.id);
            const isQuickAccepting = quickAccepting.has(d.id);
            const isDuplicate = duplicateIds.has(d.id);
            const isCommitted = d.status === "committed";
            const c = d.counters;
            // Quick-accept gate: the draft must be at rest (`ready`),
            // no failed rows, no pending or enriching rows.
            const canQuickAccept =
              !isCommitted &&
              d.status === "ready" &&
              !!c &&
              c.failed === 0 &&
              c.pending === 0 &&
              c.enriching === 0;
            return (
              <li
                key={d.id}
                className={`flex items-center gap-4 rounded-xl border border-border/60 bg-card/40 p-4 transition-all hover:border-border hover:bg-card/70 ${
                  isDeleting || isQuickAccepting ? "opacity-50" : ""
                }`}
              >
                {/* File icon wrapped in a 36×36 card per the Figma
                    polish — signals "this row IS a file" without
                    leaning on the plain icon. */}
                <div className="shrink-0 flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 bg-muted/40">
                  <FileText size={16} className="text-muted-foreground" />
                </div>
                <Link
                  to="/tools/imports/$id"
                  params={{ id: d.id }}
                  className="min-w-0 flex-1"
                >
                  <div className="flex items-baseline flex-wrap gap-2">
                    {/* Mono filename — the design treats the name as
                        a system identifier (long, dot/underscore-
                        delimited, copy-pasted) rather than prose. */}
                    <span
                      className="truncate font-mono text-[12px] text-foreground/90"
                      title={label}
                    >
                      {label}
                    </span>
                    <DraftStatusPill status={d.status} />
                    {isDuplicate && <DuplicatePill />}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {d.sourceFormat} · {d.totalRows} row
                    {d.totalRows === 1 ? "" : "s"} · created{" "}
                    {new Date(d.createdAt).toLocaleString()}
                    {isCommitted && d.committedAt && (
                      <>
                        {" · "}
                        <span className="text-success">
                          committed {new Date(d.committedAt).toLocaleString()}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Per-row enrichment summary. Hidden on committed
                      rows since counts there are frozen + not
                      actionable. */}
                  {c && !isCommitted && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <CountChip
                        label="done"
                        value={c.done + c.fixed}
                        tone="success"
                        icon={<CheckCircle2 size={9} />}
                      />
                      {c.enriching > 0 && (
                        <CountChip
                          label="enriching"
                          value={c.enriching}
                          tone="info"
                          icon={<Loader2 size={9} className="animate-spin" />}
                        />
                      )}
                      {c.pending > 0 && (
                        <CountChip
                          label="pending"
                          value={c.pending}
                          tone="muted"
                          icon={<Clock size={9} />}
                        />
                      )}
                      {c.failed > 0 && (
                        <CountChip
                          label="failed"
                          value={c.failed}
                          tone="danger"
                          icon={<AlertCircle size={9} />}
                        />
                      )}
                    </div>
                  )}
                </Link>
                {canQuickAccept && (
                  <button
                    type="button"
                    onClick={() => void handleQuickAccept(d.id, label)}
                    disabled={isQuickAccepting}
                    title="Quick accept — commits with no bin or lot"
                    aria-label={`Quick accept ${label}`}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-success/30 bg-success/10 px-3 py-1.5 text-xs font-semibold text-success transition-colors hover:bg-success/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isQuickAccepting ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Zap size={12} />
                    )}
                    {isQuickAccepting ? "Committing…" : "Quick accept"}
                  </button>
                )}
                {/* Outlined pill style — the design treats Resume /
                    View as primary actions, not tertiary text links. */}
                <Link
                  to="/tools/imports/$id"
                  params={{ id: d.id }}
                  className={`shrink-0 inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                    isCommitted
                      ? "border-primary/20 text-primary/80 hover:border-primary/40 hover:text-primary"
                      : "border-primary/30 text-primary hover:border-primary/55 hover:bg-primary/5"
                  }`}
                >
                  {isCommitted ? "View" : "Resume"}
                  <ChevronRight size={13} />
                </Link>
                {deleteConfirmId === d.id ? (
                  <>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      Delete draft?
                    </span>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(null)}
                      disabled={isDeleting}
                      className="shrink-0 inline-flex items-center rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(d.id, label)}
                      disabled={isDeleting}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Trash2 size={12} />
                      )}
                      {isDeleting ? "Deleting…" : "Yes, delete"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmId(d.id)}
                    disabled={isDeleting || isCommitted}
                    aria-label={`Delete draft ${label}`}
                    title={
                      isCommitted
                        ? "Committed drafts can't be deleted — the cards are live."
                        : "Delete draft"
                    }
                    className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-muted-foreground transition-colors hover:border-destructive/25 hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-transparent disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function TabButton({
  tab,
  current,
  count,
  children,
}: {
  tab: Tab;
  current: Tab;
  count: number;
  children: React.ReactNode;
}) {
  const active = tab === current;
  return (
    <Link
      to="/tools/imports"
      search={{ tab }}
      className={`relative -mb-px px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "border-b-2 border-primary text-foreground"
          : "border-b-2 border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
      {count > 0 && (
        <span
          className={`ml-1.5 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
            active
              ? "bg-primary/15 text-primary"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {count}
        </span>
      )}
    </Link>
  );
}

function DraftStatusPill({ status }: { status: string }) {
  const styles: Record<string, { cls: string; icon: React.ReactNode }> = {
    parsing: {
      cls: "border-border bg-muted/30 text-muted-foreground",
      icon: <Clock size={9} />,
    },
    enriching: {
      cls: "border-primary/30 bg-primary/10 text-primary",
      icon: <Loader2 size={9} className="animate-spin" />,
    },
    ready: {
      cls: "border-success/30 bg-success/10 text-success",
      icon: <CheckCircle2 size={9} />,
    },
    committed: {
      cls: "border-success/30 bg-success/10 text-success",
      icon: <CheckCircle2 size={9} />,
    },
    cancelled: {
      cls: "border-border bg-muted/30 text-muted-foreground",
      icon: <AlertCircle size={9} />,
    },
  };
  const s = styles[status] ?? styles.parsing;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${s.cls}`}
    >
      {s.icon}
      {status}
    </span>
  );
}

function DuplicatePill() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
      style={{
        background: "rgba(240,201,110,0.12)",
        borderColor: "rgba(240,201,110,0.4)",
        color: "#f0c96e",
      }}
      title="Another draft in this view shares the same filename, format, and row count."
    >
      <AlertTriangle size={9} />
      Duplicate
    </span>
  );
}

function CountChip({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number;
  tone: "success" | "info" | "muted" | "danger";
  icon?: React.ReactNode;
}) {
  const styles: Record<typeof tone, string> = {
    success: "border-success/30 bg-success/10 text-success",
    info: "border-primary/30 bg-primary/10 text-primary",
    muted: "border-border bg-muted/30 text-muted-foreground",
    danger: "border-destructive/30 bg-destructive/10 text-destructive",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 font-mono text-[10px] font-semibold ${styles[tone]}`}
    >
      {icon}
      {value}
      <span className="font-sans font-normal uppercase tracking-wider opacity-80">
        {label}
      </span>
    </span>
  );
}
